import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Header.css';
import logo from '../logo.png';

interface HeaderProps {
  title?: string;
  subtitle?: string;
  showBackButton?: boolean;
  showConnectionBadge?: boolean;
  connected?: boolean;
  showRefreshButtons?: boolean;
  isRefreshing?: boolean;
  autoRefresh?: boolean;
  onRefresh?: () => void;
  onToggleAutoRefresh?: () => void;
  lastRefresh?: string | null;
}

export const Header: React.FC<HeaderProps> = ({
  title,
  subtitle,
  showBackButton = false,
  showConnectionBadge = false,
  connected = false,
  showRefreshButtons = false,
  isRefreshing = false,
  autoRefresh = false,
  onRefresh,
  onToggleAutoRefresh,
  lastRefresh,
}) => {
  const navigate = useNavigate();
  const { user, siteConfig, logout } = useAuth();

  const formatLastRefresh = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <header className="dashboard-header">
      <div className="header-content">
        <div className="header-left">
          <div className="brand-container">
            <div className="logo-wrapper" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <img src={logo} className="header-logo" alt="HoneyCipher" />
            </div>
            <div className="header-info">
              {title ? (
                <>
                  <h1 className="header-title">{title}</h1>
                  {subtitle && <p className="header-subtitle">{subtitle}</p>}
                </>
              ) : (
                <>
                  <p className="header-subtitle">Security Monitoring Console</p>
                  {siteConfig && <p className="header-site-info">Monitoring: {siteConfig.siteUrl}</p>}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="header-right">
          {showConnectionBadge && (
            <div className={`connection-badge ${connected ? 'connected' : 'disconnected'}`}>
              <span className="connection-pulse"></span>
              <span className="connection-label">{connected ? 'LIVE' : 'OFFLINE'}</span>
            </div>
          )}

          <div className="header-actions">
            {showRefreshButtons && (
              <>
                {/* Manual refresh */}
                <button
                  onClick={onRefresh}
                  className="settings-button"
                  title="Refresh data"
                  disabled={isRefreshing}
                  style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
                >
                  {isRefreshing ? '⏳' : '🔄'}
                </button>

                {/* Auto refresh toggle */}
                <button
                  onClick={onToggleAutoRefresh}
                  className="settings-button"
                  title={autoRefresh ? 'Auto refresh: ON (click to disable)' : 'Auto refresh: OFF (click to enable)'}
                  style={{
                    borderColor: autoRefresh ? 'rgba(0,255,135,0.35)' : undefined,
                    background: autoRefresh ? 'rgba(0,255,135,0.10)' : undefined,
                  }}
                >
                  {autoRefresh ? '🟢' : '⚪'}
                </button>
              </>
            )}

            {showBackButton ? (
              <button onClick={() => navigate('/')} className="back-button">
                ← Dashboard
              </button>
            ) : (
              <>
                {/* Honeypot Config */}
                <button
                  onClick={() => navigate('/honeypot-config')}
                  className="settings-button"
                  title="Honeypot Configuration"
                >
                  🎯
                </button>

                {/* Settings */}
                <button
                  onClick={() => navigate('/settings')}
                  className="settings-button"
                  title="Settings"
                >
                  ⚙️
                </button>
              </>
            )}

            <div className="user-menu" title={lastRefresh ? `Last refresh: ${formatLastRefresh(lastRefresh)}` : undefined}>
              <span className="user-name">{user?.name}</span>
              <button onClick={logout} className="logout-btn">Logout</button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
